// Unified refund-destination picker (SPEC §5.1).
//
// Mounted at both raise (BatchReturnDamageModal / BatchRefundModal) and Authorize
// (RefundModal AUTHORIZE_RETURN context).
//
// The three destinations each occupy a visible, clickable SECTION card — no radios —
// so operators see all options at a glance and pick with one click:
//
//   • Wallet              — refund lands on the customer's wallet credit.
//   • Customer bank       — refund lands at a bank account (saved or inline-added).
//   • Not decided yet     — raise now, decide at Authorize. Available on both mounts
//                           so operators who are unsure never lose their flow; the
//                           server refuses dont_know at Authorize and the parent
//                           modal keeps Mark refund disabled with an explanation.
//
// The picker does NOT submit; the parent modal owns submission and knows which
// endpoint to hit. Picker emits a DestinationBlock + a validity flag on every change.

import {
  Alert, Button, Select, Space, Spin, Typography,
} from "antd";
import {
  BankOutlined, QuestionCircleOutlined, WalletOutlined,
} from "@ant-design/icons";
import React, { useEffect, useMemo, useState } from "react";

import { AddBankInline } from "./AddBankInline";
import type {
  Destination,
  DestinationBlock,
  NewBankDetails,
  RefundDestinationPickerProps,
} from "./types";
import { useHeadroom } from "./useHeadroom";

const { Text, Title } = Typography;

type SelectedBankMode = "saved" | "new";

function paiseToRupees(paise: number | null): string {
  if (paise === null) return "—";
  return `₹${(paise / 100).toFixed(2)}`;
}

interface SectionProps {
  active: boolean;
  disabled?: boolean;
  disabledHint?: string;
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  onClick: () => void;
  children?: React.ReactNode;
}

const Section: React.FC<SectionProps> = ({
  active, disabled, disabledHint, icon, title, subtitle, onClick, children,
}) => {
  const base: React.CSSProperties = {
    border: `1px solid ${active ? "#24AAB7" : "#e5e7eb"}`,
    borderRadius: 8,
    padding: 16,
    cursor: disabled ? "not-allowed" : "pointer",
    background: active ? "#F2FBFC" : disabled ? "#FAFAFA" : "#FFFFFF",
    boxShadow: active ? "0 0 0 2px rgba(36, 170, 183, 0.15)" : "none",
    transition: "all 120ms ease",
    opacity: disabled ? 0.6 : 1,
  };

  return (
    <div
      style={base}
      role="button"
      aria-pressed={active}
      aria-disabled={disabled}
      onClick={() => { if (!disabled) onClick(); }}
    >
      <Space align="start" style={{ width: "100%" }}>
        <div style={{ fontSize: 22, color: active ? "#24AAB7" : "#6b7280", marginTop: 2 }}>
          {icon}
        </div>
        <div style={{ flex: 1 }}>
          <Text strong style={{ fontSize: 14, display: "block" }}>{title}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>{subtitle}</Text>
          {disabled && disabledHint && (
            <div style={{ marginTop: 6 }}>
              <Text type="warning" style={{ fontSize: 11 }}>{disabledHint}</Text>
            </div>
          )}
        </div>
      </Space>
      {active && children && (
        <div style={{ marginTop: 12, borderTop: "1px dashed #e5e7eb", paddingTop: 12 }}>
          {children}
        </div>
      )}
    </div>
  );
};

export const RefundDestinationPicker: React.FC<RefundDestinationPickerProps> = ({
  mount,
  salesOrderId,
  refundAmountPaise,
  userId,
  savedBankDetails,
  initial,
  hideDontKnow,
  onChange,
}) => {
  const [destination, setDestination] = useState<Destination>(() => {
    const seed = initial?.destination ?? (mount === "raise" ? "dont_know" : "wallet");
    // When the parent mounts us with the admin pre-authorize toggle already on, the undetermined
    // card is suppressed below — start on wallet so the submit button is reachable without an
    // extra click.
    return hideDontKnow && seed === "dont_know" ? "wallet" : seed;
  });
  const [bankMode, setBankMode] = useState<SelectedBankMode>("saved");
  const [savedBankId, setSavedBankId] = useState<number | null>(
    initial?.bank_details_id ?? null
  );
  const [newBank, setNewBank] = useState<NewBankDetails | null>(
    initial?.new_bank_details ?? null
  );

  const headroom = useHeadroom(salesOrderId);
  const canBeRefundedPaise = headroom.canBeRefundedPaise;
  const headroomExceeded =
    canBeRefundedPaise !== null && refundAmountPaise > canBeRefundedPaise;

  // At the Authorize mount the server refuses dont_know. The section is still shown
  // (so operators learn what the option means) but disabled, with the reason inline.
  const dontKnowDisabled = mount === "authorize";

  // Mid-session flip: the parent flipped the admin pre-authorize toggle from No → Yes while the
  // operator had `dont_know` selected. The card goes away below, so move the selection to wallet
  // so submit stays reachable without an extra click.
  useEffect(() => {
    if (hideDontKnow && destination === "dont_know") {
      setDestination("wallet");
    }
  }, [hideDontKnow, destination]);

  const block: DestinationBlock = useMemo(() => {
    if (destination === "customer") {
      if (bankMode === "saved" && savedBankId !== null) {
        return { destination: "customer", bank_details_id: savedBankId };
      }
      if (bankMode === "new" && newBank) {
        return { destination: "customer", new_bank_details: newBank };
      }
      return { destination: "customer" };
    }
    return { destination };
  }, [destination, bankMode, savedBankId, newBank]);

  const isValid = useMemo(() => {
    if (headroomExceeded) return false;
    if (destination === "dont_know") return !dontKnowDisabled;
    if (destination === "customer") {
      if (bankMode === "saved") return savedBankId !== null;
      return newBank !== null;
    }
    return true;
  }, [destination, bankMode, savedBankId, newBank, headroomExceeded, dontKnowDisabled]);

  useEffect(() => {
    onChange(block, isValid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [block, isValid]);

  const bankOptions = savedBankDetails.map((b) => ({
    value: b.id,
    label: `${b.bank_name} · ${b.account_holder_name} · …${b.account_number.slice(-4)}`,
  }));

  return (
    <Space direction="vertical" style={{ width: "100%" }} size="middle">
      <Space align="baseline" style={{ justifyContent: "space-between", width: "100%" }}>
        <Title level={5} style={{ margin: 0 }}>Where should this refund land?</Title>
        <Text
          type={headroomExceeded ? "danger" : "secondary"}
          style={{ fontSize: 12 }}
        >
          {headroom.loading ? (
            <Spin size="small" />
          ) : headroom.error ? (
            `Could not load headroom (${headroom.error})`
          ) : (
            <>Can be refunded: <strong>{paiseToRupees(canBeRefundedPaise)}</strong></>
          )}
        </Text>
      </Space>

      <Space direction="vertical" style={{ width: "100%" }} size={10}>
        <Section
          active={destination === "wallet"}
          icon={<WalletOutlined />}
          title="Wallet"
          subtitle="Refund the customer's wallet balance. Instant, no bank details needed."
          onClick={() => setDestination("wallet")}
        />

        <Section
          active={destination === "customer"}
          icon={<BankOutlined />}
          title="Customer bank"
          subtitle="Finance wires the refund to the customer's bank account."
          onClick={() => setDestination("customer")}
        >
          <Space direction="vertical" style={{ width: "100%" }} size={8}>
            <Space size="middle">
              <Button
                size="small"
                type={bankMode === "saved" ? "primary" : "default"}
                ghost={bankMode === "saved"}
                onClick={() => setBankMode("saved")}
              >
                Pick saved account
              </Button>
              <Button
                size="small"
                type={bankMode === "new" ? "primary" : "default"}
                ghost={bankMode === "new"}
                onClick={() => setBankMode("new")}
              >
                Add a new account
              </Button>
            </Space>

            {bankMode === "saved" && (
              <>
                <Select
                  placeholder={
                    savedBankDetails.length === 0
                      ? userId
                        ? "No saved accounts for this customer"
                        : "User id missing — cannot load accounts"
                      : "Select an account"
                  }
                  options={bankOptions}
                  value={savedBankId ?? undefined}
                  onChange={(v) => setSavedBankId(v)}
                  style={{ width: "100%" }}
                  disabled={savedBankDetails.length === 0}
                />
                {savedBankDetails.length === 0 && (
                  <Button type="link" style={{ paddingLeft: 0 }}
                    onClick={() => setBankMode("new")}>
                    Add a new account instead
                  </Button>
                )}
              </>
            )}

            {bankMode === "new" && (
              <AddBankInline value={newBank} onChange={setNewBank} />
            )}
          </Space>
        </Section>

        {!hideDontKnow && (
          <Section
            active={destination === "dont_know"}
            disabled={dontKnowDisabled}
            disabledHint={
              dontKnowDisabled
                ? "A destination must be chosen when authorising the refund — this option is only available at raise."
                : undefined
            }
            icon={<QuestionCircleOutlined />}
            title="Not decided yet"
            subtitle="Raise the refund now and let the Authorize step pick the destination later."
            onClick={() => setDestination("dont_know")}
          />
        )}
      </Space>

      {headroomExceeded && (
        <Alert
          type="error"
          showIcon
          message={`This refund (${paiseToRupees(refundAmountPaise)}) exceeds what can be refunded on this order (${paiseToRupees(canBeRefundedPaise)}).`}
        />
      )}
    </Space>
  );
};

export type {
  Destination,
  DestinationBlock,
  Mount,
  NewBankDetails,
  RefundDestinationPickerProps,
} from "./types";
